import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { measurementsAPI, customersAPI } from '../services/api';
import { FiArrowLeft } from 'react-icons/fi';

const EditMeasurement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    garment_type: 'Shirt',
    chest: '',
    waist: '',
    hips: '',
    shoulder_width: '',
    sleeve_length: '',
    shirt_length: '',
    pant_length: '',
    inseam: '',
    outseam: '',
    neck_size: '',
    bicep: '',
    forearm: '',
    thigh: '',
    knee: '',
    ankle: '',
    notes: '',
    measurement_date: new Date().toISOString().split('T')[0],
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchMeasurement = useCallback(async () => {
    try {
      const response = await measurementsAPI.getById(id);
      const data = response.data;
      setFormData({
        ...data,
        measurement_date: data.measurement_date ? new Date(data.measurement_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
    } catch (error) {
      console.error('Error fetching measurement:', error);
      alert('Error loading measurement data');
    } finally {
      setFetching(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomers();
    fetchMeasurement();
  }, [id, fetchCustomers, fetchMeasurement]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.garment_type) {
      alert('Customer and garment type are required');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        measurement_date: formData.measurement_date ? new Date(formData.measurement_date).toISOString() : new Date().toISOString(),
      };
      await measurementsAPI.update(id, submitData);
      navigate(`/customers/${formData.customer_id}`);
    } catch (error) {
      console.error('Error updating measurement:', error);
      alert('Error updating measurement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const garmentTypes = ['Shirt', 'Pant', 'Suit', 'Blazer', 'Kurta', 'Sherwani', 'Dress', 'Other'];

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => navigate(`/customers/${formData.customer_id}`)}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6"
      >
        <FiArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Edit Measurement</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Garment Type <span className="text-red-500">*</span>
              </label>
              <select
                name="garment_type"
                value={formData.garment_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {garmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Date
              </label>
              <input
                type="date"
                name="measurement_date"
                value={formData.measurement_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Body Measurements (in inches)</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {['chest', 'waist', 'hips', 'shoulder_width', 'neck_size', 'bicep', 'forearm'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {field.replace('_', ' ')}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.0"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Shirt Measurements</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {['sleeve_length', 'shirt_length'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {field.replace('_', ' ')}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.0"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pant Measurements</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {['pant_length', 'inseam', 'outseam', 'thigh', 'knee', 'ankle'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {field.replace('_', ' ')}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.0"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Measurement'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/customers/${formData.customer_id}`)}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMeasurement;

